/**
 * AdminManualSignatures — DSGVO-Audit-Übersicht aller manuell erfassten Signaturen.
 * (Iter88zv) Filter: Kanal + Zeitraum. Klick springt zum Projekt.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Filter, ExternalLink, Mail, FileText, Download } from "lucide-react";
import api from "@/lib/api";

const CHANNEL_LABELS = {
  "manual-email": "E-Mail",
  "manual-post": "Post",
  "manual-fax": "Fax",
  "manual-in_person": "Persönlich",
  "manual-phone_confirmed": "Telefonisch",
};

const fmtEur = (n) => new Intl.NumberFormat("de-DE", {
  style: "currency", currency: "EUR", maximumFractionDigits: 0,
}).format(Number(n || 0));

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function AdminManualSignatures() {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (channel) params.channel = channel;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo + "T23:59:59").toISOString();
      const { data } = await api.get("/admin/signatures/manual", { params });
      setSignatures(data.signatures || []);
    } catch { toast.error("Laden fehlgeschlagen"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportCsv = () => {
    const rows = [
      ["Angebot", "Kunde", "Unterzeichner", "Kanal", "Signiert", "Erfasst am", "Erfasst durch", "Belege", "Auftragssumme"],
      ...signatures.map(s => [
        s.offer_number, s.customer_name, s.signer_name,
        CHANNEL_LABELS[s.channel] || s.channel,
        fmtDT(s.signed_at), fmtDT(s.registered_at), s.registered_by_name || "",
        s.evidence_files_count, s.total_gross.toFixed(2),
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `manuelle-signaturen-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-4 sm:p-6" data-testid="admin-manual-signatures-page">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h1 className="text-2xl font-black text-zinc-100">Manuelle Signaturen (DSGVO-Audit)</h1>
        <span className="text-[12px] text-zinc-500 ml-2">{signatures.length} Einträge</span>
        <button
          onClick={exportCsv}
          disabled={signatures.length === 0}
          className="ml-auto h-9 px-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-40"
          data-testid="signatures-export-csv"
        >
          <Download size={13} /> CSV exportieren
        </button>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3 mb-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
        <div>
          <label className="text-[10.5px] text-zinc-400 uppercase font-bold">Kanal</label>
          <select value={channel} onChange={(e) => setChannel(e.target.value)}
            className="w-full h-10 rounded bg-zinc-800 border border-zinc-700 px-2 text-[13px] text-zinc-100"
            data-testid="signatures-filter-channel">
            <option value="">Alle Kanäle</option>
            {Object.entries(CHANNEL_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10.5px] text-zinc-400 uppercase font-bold">Von</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="w-full h-10 rounded bg-zinc-800 border border-zinc-700 px-2 text-[13px] text-zinc-100"
            data-testid="signatures-filter-from" />
        </div>
        <div>
          <label className="text-[10.5px] text-zinc-400 uppercase font-bold">Bis</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="w-full h-10 rounded bg-zinc-800 border border-zinc-700 px-2 text-[13px] text-zinc-100"
            data-testid="signatures-filter-to" />
        </div>
        <button onClick={load}
          className="h-10 px-4 rounded bg-blue-500 hover:bg-blue-400 text-white text-[12px] font-bold inline-flex items-center gap-1.5"
          data-testid="signatures-filter-apply">
          <Filter size={13} /> Filtern
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500"><Loader2 className="animate-spin inline mr-2" size={14} /> Lade …</div>
      ) : signatures.length === 0 ? (
        <div className="text-center py-12 text-zinc-500 border border-dashed border-zinc-800 rounded-lg">
          Keine Signaturen für die aktuellen Filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="w-full text-[12.5px]">
            <thead className="bg-zinc-800/60 text-zinc-400 text-[10.5px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Angebot</th>
                <th className="text-left px-3 py-2">Kunde / Unterzeichner</th>
                <th className="text-left px-3 py-2">Kanal</th>
                <th className="text-left px-3 py-2">Signiert am</th>
                <th className="text-left px-3 py-2">Erfasst am / durch</th>
                <th className="text-center px-3 py-2">Belege</th>
                <th className="text-right px-3 py-2">Auftrag</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {signatures.map((s) => (
                <tr key={s.offer_id} className="border-t border-zinc-800 hover:bg-zinc-900/50" data-testid={`signature-row-${s.offer_id}`}>
                  <td className="px-3 py-2 font-mono text-[12px] text-emerald-400">{s.offer_number}</td>
                  <td className="px-3 py-2">
                    <div className="font-bold text-zinc-100">{s.customer_name || "—"}</div>
                    <div className="text-[11px] text-zinc-500">Unterzeichner: {s.signer_name || "—"}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="px-1.5 py-0.5 rounded bg-blue-500/25 text-[10.5px] font-bold text-blue-300 inline-flex items-center gap-1">
                      <Mail size={9} /> {CHANNEL_LABELS[s.channel] || s.channel}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-zinc-300">{fmtDT(s.signed_at)}</td>
                  <td className="px-3 py-2 text-zinc-400 text-[11.5px]">
                    {fmtDT(s.registered_at)}<br/>
                    <span className="text-zinc-500">durch {s.registered_by_name || "—"}</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {s.evidence_files_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-blue-300 font-bold">
                        <FileText size={11} /> {s.evidence_files_count}
                      </span>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-300">{fmtEur(s.total_gross)}</td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/projects/${s.project_id}`} className="text-blue-300 hover:text-blue-200" data-testid={`signature-goto-${s.offer_id}`}>
                      <ExternalLink size={14} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
